import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Users, Shield, Car, Store, UserX, Loader2, Phone, Mail, IdCard, Calendar, Clock, X, ChevronRight } from 'lucide-react';
interface UserSummary {
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  national_id: string | null;
  profile_is_active: boolean;
  is_platform_admin: boolean;
  platform_role: string | null;
  is_driver: boolean;
  driver_id: number | null;
  has_restaurant_access: boolean;
  restaurant_access_count: number;
  restaurant_roles: string[] | null;
  auth_created_at: string;
  last_sign_in_at: string | null;
}
export function UsersAdminPage() {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filters
  const [filterAdmin, setFilterAdmin] = useState(false);
  const [filterDriver, setFilterDriver] = useState(false);
  const [filterRestaurant, setFilterRestaurant] = useState(false);
  const [filterInactive, setFilterInactive] = useState(false);
  // Drawer state
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  useEffect(() => {
    fetchUsers();
  }, []);
  const fetchUsers = async () => {
    setLoading(true);
    const {
      data,
      error
    } = await supabase.from('v_user_access_summary').select('*').order('auth_created_at', {
      ascending: false
    });
    if (!error && data) {
      setUsers(data);
    } else {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  };
  // Apply filters and search
  const filteredUsers = users.filter(u => {
    // Search
    const s = search.toLowerCase();
    const matchesSearch = !s || u.full_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.phone?.includes(s) || u.national_id?.includes(s);
    if (!matchesSearch) return false;
    // Toggles (if any toggle is active, user must match ALL active toggles)
    if (filterAdmin && !u.is_platform_admin) return false;
    if (filterDriver && !u.is_driver) return false;
    if (filterRestaurant && !u.has_restaurant_access) return false;
    if (filterInactive && u.profile_is_active) return false;
    return true;
  });
  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Nunca';
    return new Date(isoString).toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex" data-id="element-5985">
      <div className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-5986">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-5987">
          <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-5988">
            <Users className="w-6 h-6 text-[#006847]" data-id="element-5989" />
          </div>
          <div data-id="element-5990">
            <h1 className="text-2xl font-bold" data-id="element-5991">Usuarios del Sistema</h1>
            <p className="text-white/40 text-sm" data-id="element-5992">
              {filteredUsers.length}{' '}
              {filteredUsers.length === 1 ? 'usuario' : 'usuarios'} encontrados
            </p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-4" data-id="element-5993">
          <div className="relative" data-id="element-5994">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-5995" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, email, teléfono o cédula..." className="w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-5996" />
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-5997">
            <button onClick={() => setFilterAdmin(!filterAdmin)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterAdmin ? 'bg-[#006847] text-white' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-5998">
              <Shield className="w-3.5 h-3.5" data-id="element-5999" /> Admin NOIZA
            </button>
            <button onClick={() => setFilterDriver(!filterDriver)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterDriver ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6000">
              <Car className="w-3.5 h-3.5" data-id="element-6001" /> Conductores
            </button>
            <button onClick={() => setFilterRestaurant(!filterRestaurant)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterRestaurant ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6002">
              <Store className="w-3.5 h-3.5" data-id="element-6003" /> Restaurante
            </button>
            <button onClick={() => setFilterInactive(!filterInactive)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterInactive ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6004">
              <UserX className="w-3.5 h-3.5" data-id="element-6005" /> Inactivos
            </button>
          </div>
        </div>

        {/* List */}
        {loading ? <div className="flex justify-center py-20" data-id="element-6006">
            <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-6007" />
          </div> : filteredUsers.length === 0 ? <div className="text-center py-20 bg-[#141414] border border-white/10 rounded-2xl" data-id="element-6008">
            <Users className="w-12 h-12 text-white/10 mx-auto mb-4" data-id="element-6009" />
            <p className="text-white/40 text-lg font-medium" data-id="element-6010">
              No se encontraron usuarios
            </p>
          </div> : <div className="grid gap-3" data-id="element-6011">
            {filteredUsers.map(user => <div key={user.user_id} onClick={() => setSelectedUser(user)} className="bg-[#141414] border border-white/10 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors group" data-id="element-6012">
                {/* Avatar */}
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center shrink-0 text-xl font-black text-white/30" data-id="element-6013">
                  {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" data-id="element-6014">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6015">
                    <h3 className="font-bold text-white truncate" data-id="element-6016">
                      {user.full_name || 'Sin nombre'}
                    </h3>
                    {!user.profile_is_active && <span className="bg-red-500/10 text-red-400 text-[10px] uppercase font-black px-2 py-0.5 rounded-md shrink-0" data-id="element-6017">
                        Inactivo
                      </span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/50 truncate" data-id="element-6018">
                    <span className="truncate" data-id="element-6019">{user.email}</span>
                    {user.phone && <>
                        <span className="w-1 h-1 bg-white/20 rounded-full shrink-0" data-id="element-6020" />
                        <span className="shrink-0" data-id="element-6021">{user.phone}</span>
                      </>}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-2" data-id="element-6022">
                    {user.is_platform_admin && <span className="inline-flex items-center gap-1 bg-[#006847]/20 text-[#00a870] text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6023">
                        <Shield className="w-3 h-3" data-id="element-6024" /> Admin NOIZA
                      </span>}
                    {user.is_driver && <span className="inline-flex items-center gap-1 bg-blue-500/10 text-blue-400 text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6025">
                        <Car className="w-3 h-3" data-id="element-6026" /> Conductor
                      </span>}
                    {user.has_restaurant_access && <span className="inline-flex items-center gap-1 bg-purple-500/10 text-purple-400 text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6027">
                        <Store className="w-3 h-3" data-id="element-6028" /> Restaurante (
                        {user.restaurant_access_count})
                      </span>}
                  </div>
                </div>

                <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/50 transition-colors shrink-0" data-id="element-6029" />
              </div>)}
          </div>}
      </div>

      {/* Detail Drawer */}
      {selectedUser && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setSelectedUser(null)} data-id="element-6030" />

          {/* Drawer */}
          <div className="fixed inset-y-0 right-0 w-full max-w-md bg-[#141414] border-l border-white/10 z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200" data-id="element-6031">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-6032">
              <h2 className="text-lg font-bold" data-id="element-6033">Detalles del Usuario</h2>
              <button onClick={() => setSelectedUser(null)} className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-id="element-6034">
                <X className="w-4 h-4" data-id="element-6035" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-6036">
              {/* Profile Header */}
              <div className="flex items-center gap-4" data-id="element-6037">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center text-2xl font-black text-white/30" data-id="element-6038">
                  {selectedUser.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div data-id="element-6039">
                  <h3 className="text-xl font-bold text-white" data-id="element-6040">
                    {selectedUser.full_name || 'Sin nombre'}
                  </h3>
                  <p className="text-white/50 text-sm" data-id="element-6041">{selectedUser.email}</p>
                </div>
              </div>

              {/* Roles Summary */}
              <div className="space-y-3" data-id="element-6042">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6043">
                  Roles en la plataforma
                </h4>
                <div className="grid gap-2" data-id="element-6044">
                  {selectedUser.is_platform_admin && <div className="bg-[#006847]/10 border border-[#006847]/20 rounded-xl p-3 flex items-center gap-3" data-id="element-6045">
                      <div className="w-8 h-8 bg-[#006847]/20 rounded-lg flex items-center justify-center" data-id="element-6046">
                        <Shield className="w-4 h-4 text-[#00a870]" data-id="element-6047" />
                      </div>
                      <div data-id="element-6048">
                        <p className="font-bold text-[#00a870] text-sm" data-id="element-6049">
                          Admin NOIZA
                        </p>
                        <p className="text-xs text-[#00a870]/70" data-id="element-6050">
                          {selectedUser.platform_role || 'Administrador'}
                        </p>
                      </div>
                    </div>}

                  {selectedUser.is_driver && <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 flex items-center gap-3" data-id="element-6051">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center" data-id="element-6052">
                        <Car className="w-4 h-4 text-blue-400" data-id="element-6053" />
                      </div>
                      <div className="flex-1" data-id="element-6054">
                        <p className="font-bold text-blue-400 text-sm" data-id="element-6055">
                          Conductor
                        </p>
                        <p className="text-xs text-blue-400/70" data-id="element-6056">
                          ID: {selectedUser.driver_id}
                        </p>
                      </div>
                    </div>}

                  {selectedUser.has_restaurant_access && <div className="bg-purple-500/5 border border-purple-500/10 rounded-xl p-3 flex items-center gap-3" data-id="element-6057">
                      <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center" data-id="element-6058">
                        <Store className="w-4 h-4 text-purple-400" data-id="element-6059" />
                      </div>
                      <div data-id="element-6060">
                        <p className="font-bold text-purple-400 text-sm" data-id="element-6061">
                          Acceso a Restaurantes
                        </p>
                        <p className="text-xs text-purple-400/70" data-id="element-6062">
                          {selectedUser.restaurant_access_count} restaurante(s)
                        </p>
                      </div>
                    </div>}

                  {!selectedUser.is_platform_admin && !selectedUser.is_driver && !selectedUser.has_restaurant_access && <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center" data-id="element-6063">
                        <p className="text-sm text-white/40" data-id="element-6064">
                          Sin roles asignados
                        </p>
                      </div>}
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-3" data-id="element-6065">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6066">
                  Información Personal
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4" data-id="element-6067">
                  <div className="flex items-center gap-3" data-id="element-6068">
                    <Phone className="w-4 h-4 text-white/30" data-id="element-6069" />
                    <div data-id="element-6070">
                      <p className="text-xs text-white/40" data-id="element-6071">Teléfono</p>
                      <p className="text-sm font-medium" data-id="element-6072">
                        {selectedUser.phone || 'No registrado'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6073">
                    <IdCard className="w-4 h-4 text-white/30" data-id="element-6074" />
                    <div data-id="element-6075">
                      <p className="text-xs text-white/40" data-id="element-6076">Cédula</p>
                      <p className="text-sm font-medium" data-id="element-6077">
                        {selectedUser.national_id || 'No registrada'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6078">
                    <UserX className="w-4 h-4 text-white/30" data-id="element-6079" />
                    <div data-id="element-6080">
                      <p className="text-xs text-white/40" data-id="element-6081">Estado del perfil</p>
                      <p className={`text-sm font-bold ${selectedUser.profile_is_active ? 'text-[#00a870]' : 'text-red-400'}`} data-id="element-6082">
                        {selectedUser.profile_is_active ? 'Activo' : 'Inactivo'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Info */}
              <div className="space-y-3" data-id="element-6083">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6084">
                  Información del Sistema
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4" data-id="element-6085">
                  <div className="flex items-center gap-3" data-id="element-6086">
                    <Calendar className="w-4 h-4 text-white/30" data-id="element-6087" />
                    <div data-id="element-6088">
                      <p className="text-xs text-white/40" data-id="element-6089">Fecha de registro</p>
                      <p className="text-sm font-medium" data-id="element-6090">
                        {formatDate(selectedUser.auth_created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6091">
                    <Clock className="w-4 h-4 text-white/30" data-id="element-6092" />
                    <div data-id="element-6093">
                      <p className="text-xs text-white/40" data-id="element-6094">Último acceso</p>
                      <p className="text-sm font-medium" data-id="element-6095">
                        {formatDate(selectedUser.last_sign_in_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6096">
                    <Shield className="w-4 h-4 text-white/30" data-id="element-6097" />
                    <div className="min-w-0" data-id="element-6098">
                      <p className="text-xs text-white/40" data-id="element-6099">Auth ID</p>
                      <p className="text-xs font-mono text-white/70 truncate" data-id="element-6100">
                        {selectedUser.user_id}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}